import { useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Download, Send } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const inputCls =
  "w-full bg-transparent border-b border-[#1A1A1A]/25 py-2 text-sm font-light placeholder:text-[#1A1A1A]/35 focus:outline-none focus:border-[#4A148C] transition-colors duration-300";

export const EmailCentre = ({ token }) => {
  const headers = { Authorization: `Bearer ${token}` };
  const [overview, setOverview] = useState(null);
  const [audience, setAudience] = useState([]);
  const [templates, setTemplates] = useState([]);
  const [recipient, setRecipient] = useState("");
  const [templateId, setTemplateId] = useState("follow_up");
  const [note, setNote] = useState("");
  const [sending, setSending] = useState(false);

  const load = async () => {
    try {
      const [ov, au, tp] = await Promise.all([
        axios.get(`${API}/admin/email/overview`, { headers }),
        axios.get(`${API}/admin/email/audience`, { headers }),
        axios.get(`${API}/admin/email/templates`, { headers }),
      ]);
      setOverview(ov.data);
      setAudience(au.data);
      setTemplates(tp.data);
    } catch {
      toast.error("Couldn't load the email centre");
    }
  };

  useEffect(() => { load(); }, []);

  const exportCsv = async () => {
    try {
      const res = await axios.get(`${API}/admin/email/export`, { headers, responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url;
      a.download = "proppa-audience.csv";
      a.click();
      URL.revokeObjectURL(url);
    } catch {
      toast.error("Export failed");
    }
  };

  const sendOne = async () => {
    if (!recipient.trim() || !recipient.includes("@")) {
      toast.error("Enter the recipient's email");
      return;
    }
    setSending(true);
    try {
      await axios.post(`${API}/admin/email/send-one`,
        { recipient_email: recipient.trim(), template_id: templateId, note: note.trim() || null },
        { headers });
      toast.success(`Email sent to ${recipient.trim()}`);
      setRecipient("");
      setNote("");
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Send failed");
    } finally {
      setSending(false);
    }
  };

  const stats = overview
    ? [
        { label: "Newsletter signups", value: overview.subscribers, testid: "stat-subscribers" },
        { label: "Past customers", value: overview.customers, testid: "stat-customers" },
        { label: "Basket reminders sent", value: overview.basket_reminders_sent, testid: "stat-reminders" },
        { label: "Review emails sent", value: overview.review_emails_sent, testid: "stat-reviews" },
      ]
    : [];

  return (
    <div className="space-y-10" data-testid="email-centre">
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
        {stats.map((s) => (
          <div key={s.label} className="border border-[#E5DFD3] bg-[#FDFBF7] p-5" data-testid={s.testid}>
            <p className="text-3xl font-light">{s.value}</p>
            <p className="text-[10px] font-semibold tracking-[0.2em] uppercase text-[#1A1A1A]/45 mt-1">{s.label}</p>
          </div>
        ))}
      </div>

      <div className="border border-[#E5DFD3] bg-[#FDFBF7] p-6">
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-xs font-semibold tracking-[0.25em] uppercase text-[#1A1A1A]/60">Your audience</h3>
          <button onClick={exportCsv} data-testid="export-audience-btn" className="inline-flex items-center gap-2 border border-[#1A1A1A]/30 px-4 py-2 text-[10px] font-semibold tracking-[0.2em] uppercase hover:border-[#4A148C] hover:text-[#4A148C] transition-colors duration-300">
            <Download size={12} /> Export CSV
          </button>
        </div>
        <p className="text-xs text-[#1A1A1A]/50 mb-4">
          Everyone who's ordered or signed up. Export this list to use in your newsletter platform for big marketing sends.
        </p>
        <div className="max-h-64 overflow-y-auto divide-y divide-[#E5DFD3]" data-testid="audience-list">
          {audience.length === 0 && <p className="text-sm text-[#1A1A1A]/40 py-3">Nobody here yet — signups and customers will appear automatically.</p>}
          {audience.map((p) => (
            <div key={p.email} className="flex justify-between items-center py-2 text-sm">
              <span className="font-medium">{p.email}</span>
              <span className="text-xs text-[#1A1A1A]/45">
                {p.source}{!p.active && " · unsubscribed"}
              </span>
            </div>
          ))}
        </div>
      </div>

      <div className="border border-[#E5DFD3] bg-[#FDFBF7] p-6">
        <h3 className="text-xs font-semibold tracking-[0.25em] uppercase text-[#1A1A1A]/60 mb-4">Send a one-off email</h3>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-8 gap-y-4">
          <input value={recipient} onChange={(e) => setRecipient(e.target.value)} placeholder="Recipient email (must be a customer/signup)" className={inputCls} data-testid="one-off-recipient" />
          <select value={templateId} onChange={(e) => setTemplateId(e.target.value)} className="border-b border-[#1A1A1A]/25 py-2 text-sm font-light bg-transparent focus:outline-none focus:border-[#4A148C]" data-testid="one-off-template">
            {templates.map((t) => (
              <option key={t.id} value={t.id}>{t.label} — “{t.subject}”</option>
            ))}
          </select>
          <textarea value={note} onChange={(e) => setNote(e.target.value)} placeholder="Optional personal note (added to the email)" rows={3} className="border border-[#1A1A1A]/25 bg-transparent p-3 text-sm font-light placeholder:text-[#1A1A1A]/35 focus:outline-none focus:border-[#4A148C] sm:col-span-2" data-testid="one-off-note" />
        </div>
        <div className="mt-4 flex justify-end">
          <button onClick={sendOne} disabled={sending} data-testid="one-off-send-btn" className="inline-flex items-center gap-2 bg-[#1A1A1A] text-[#FDFBF7] px-6 py-2 text-[10px] font-semibold tracking-[0.2em] uppercase hover:bg-[#4A148C] disabled:opacity-50 transition-colors duration-300">
            <Send size={12} /> {sending ? "Sending…" : "Send email"}
          </button>
        </div>
      </div>

      <div className="border border-[#E5DFD3] bg-[#FFF4CC] p-6">
        <h3 className="text-xs font-semibold tracking-[0.25em] uppercase text-[#1A1A1A]/60 mb-2">Automatic emails running</h3>
        <ul className="text-sm text-[#1A1A1A]/70 space-y-1 list-disc pl-5">
          <li><strong>Basket reminders</strong> — if someone starts checkout, leaves their email, and doesn't pay, they get a nudge after 2 hours</li>
          <li><strong>Review requests</strong> — 3 days after you mark an order dispatched, the customer gets a "how did we do?" email</li>
          <li><strong>Welcome email</strong> — every newsletter signup gets an instant hello</li>
        </ul>
      </div>
    </div>
  );
};
